import { ReactNode, useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import Loader from '../Loader/Loader';

// Sections used to appear one by one as their queries resolved, each pushing
// the layout around. The page is therefore built behind an opaque cover and
// shown in one piece once its data has settled.
//
// The children stay mounted the whole time - they are covered, not unmounted -
// so requests and images are already on their way when the cover comes off.

// Queries are started in chains (a component mounts, then asks for its data),
// so a short quiet period is required before calling the page ready.
const SETTLE_MS = 150;
// If nothing has even started, there is nothing to wait for - a route serving
// everything from cache must not sit behind the cover.
const NOTHING_STARTED_MS = 1500;
// Never leave a visitor staring at a cover, whatever happens to the requests.
const MAX_WAIT_MS = 6000;

const PageReveal = ({ children }: { children: ReactNode }) => {
  const fetching = useIsFetching();
  const [revealed, setRevealed] = useState(false);
  const anyRequestStarted = useRef(false);

  if (fetching > 0) {
    anyRequestStarted.current = true;
  }

  useEffect(() => {
    if (revealed) {
      return;
    }
    const safetyValve = setTimeout(() => setRevealed(true), MAX_WAIT_MS);
    return () => clearTimeout(safetyValve);
  }, [revealed]);

  useEffect(() => {
    if (revealed || fetching > 0) {
      return;
    }
    const settled = setTimeout(
      () => setRevealed(true),
      anyRequestStarted.current ? SETTLE_MS : NOTHING_STARTED_MS
    );
    return () => clearTimeout(settled);
  }, [fetching, revealed]);

  return (
    <>
      {children}
      {!revealed && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            // Matches the page background, so uncovering is not a colour change.
            backgroundColor: '#f5f7fa',
            zIndex: 100000,
          }}
        >
          <Loader />
        </div>
      )}
    </>
  );
};

export default PageReveal;
